import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { sanitizeCssColor } from "@/lib/security";
import type { PresenceUser } from "@/hooks/useProjectWebSocket";

interface UserPresenceProps {
  users: PresenceUser[];
  maxVisible?: number;
}

const MAX_VISIBLE_DEFAULT = 5;

/**
 * Displays colored avatar circles for users currently online in a project.
 * Shows up to `maxVisible` avatars with a "+N" overflow indicator.
 */
export const UserPresence = ({
  users,
  maxVisible = MAX_VISIBLE_DEFAULT,
}: UserPresenceProps) => {
  const { t } = useTranslation("common");

  const { visible, overflow } = useMemo(() => {
    if (users.length <= maxVisible) {
      return { visible: users, overflow: 0 };
    }
    return {
      visible: users.slice(0, maxVisible),
      overflow: users.length - maxVisible,
    };
  }, [users, maxVisible]);

  if (users.length === 0) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="flex items-center"
        aria-label={t("presence_online_users")}
      >
        {visible.map((user, index) => {
          const displayName = user.name?.trim() || "User";
          const initials =
            displayName
              .split(" ")
              .flatMap((part) => (part[0] ? [part[0]] : []))
              .join("")
              .slice(0, 2)
              .toUpperCase() || "?";

          return (
            <Tooltip key={user.id}>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    "relative flex size-8 items-center justify-center rounded-full border-2 border-white dark:border-gray-900 text-[11px] font-semibold text-white cursor-default select-none",
                    index > 0 && "-ml-2"
                  )}
                  style={{ backgroundColor: sanitizeCssColor(user.avatar_color) }}
                >
                  {initials}
                  <span
                    className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-green-500 border-2 border-white dark:border-gray-900"
                    aria-hidden="true"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="bg-white dark:bg-gray-900 text-foreground border shadow-md"
              >
                <p className="text-xs font-medium">{displayName}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}

        {overflow > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "relative flex size-8 items-center justify-center rounded-full border-2 border-white dark:border-gray-900 bg-gray-400 dark:bg-gray-600 text-[11px] font-semibold text-white cursor-default select-none",
                  visible.length > 0 && "-ml-2"
                )}
              >
                +{overflow}
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="bg-white dark:bg-gray-900 text-foreground border shadow-md"
            >
              <div className="space-y-1">
                {users.slice(maxVisible).map((user) => (
                  <p key={user.id} className="text-xs font-medium">
                    {user.name?.trim() || "User"}
                  </p>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};
